import clsx from 'clsx';
import type { JSX, ChangeEvent } from 'react';
import { useApp } from '../../hooks/useApp';

interface DistributorSelectorProps {
  className?: string;
}

/**
 * Distributor selector for Purchasing domain pages.
 * Shows as a styled selector to support multiple distributors in future.
 * Currently renders BLVS as only option.
 */
export default function DistributorSelector({
  className,
}: DistributorSelectorProps): JSX.Element | null {
  const { activeDistributor, distributors, setActiveDistributor } = useApp();

  if (!activeDistributor) return null;

  if (distributors.length > 1) {
    return (
      <select
        value={activeDistributor.id}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          // `.find()` cannot return undefined here — `e.target.value` is always
          // one of the ids we just rendered as an option.
          setActiveDistributor(distributors.find((d) => d.id === e.target.value)!)
        }
        className={clsx(
          'text-xs bg-surface-3 border border-border rounded px-2.5 py-1',
          'text-ink-secondary font-medium outline-none focus:border-accent cursor-pointer',
          className
        )}
        title={activeDistributor.name}
      >
        {distributors.map((d) => (
          <option key={d.id} value={d.id}>
            {d.code}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div
      className={clsx(
        'inline-flex items-center px-2.5 py-1 rounded bg-surface-3 border border-border',
        className
      )}
      title={activeDistributor.name}
    >
      <span className="text-xs text-ink-secondary font-medium font-mono">
        {activeDistributor.code}
      </span>
    </div>
  );
}
